/**
 * Campaign Flow Service
 * 
 * Manages the current campaign's flow configuration and provides
 * access to step content throughout the Experience app.
 */

import { Injectable, signal, computed } from '@angular/core';
import {
  Campaign,
  CampaignFlow,
  FlowStep,
  StepContent,
  generateDefaultFlow,
} from '@kanek/shared-data-access';

@Injectable({
  providedIn: 'root',
})
export class CampaignFlowService {
  // Current campaign flow
  private _flow = signal<CampaignFlow | null>(null);
  
  // Current campaign (for accessing game type, etc.)
  private _campaign = signal<Campaign | null>(null);
  
  // Public read-only signals
  readonly flow = this._flow.asReadonly();
  readonly campaign = this._campaign.asReadonly();
  
  // Enabled steps in order
  readonly enabledSteps = computed(() => {
    const flow = this._flow();
    if (!flow) return [];
    
    return flow.steps
      .filter(step => step.enabled)
      .sort((a, b) => a.order - b.order);
  });
  
  /**
   * Initializes the campaign flow
   * Generates default flow if campaign doesn't have one
   */
  initializeFlow(campaign: Partial<Campaign>): void {
    this._campaign.set(campaign as Campaign);
    
    // Use campaign's flow or generate default
    const flow = campaign.flow || generateDefaultFlow({
      campaignId: campaign.id || 'temp',
      gameType: campaign.gameType || 'wheel',
    });
    
    this._flow.set(flow);
    
    console.log('🎯 Campaign flow initialized:', {
      campaignId: campaign.id,
      stepCount: flow.steps.length,
      enabledSteps: flow.steps.filter(s => s.enabled).length,
    });
  }
  
  /**
   * Gets step content by step type
   */
  getStepContent(stepType: string): StepContent | null {
    const flow = this._flow();
    if (!flow) return null;
    
    const step = flow.steps.find(s => s.type === stepType && s.enabled);
    return step?.content || null;
  }
  
  /**
   * Gets a specific step by type
   */
  getStep(stepType: string): FlowStep | null {
    const flow = this._flow();
    if (!flow) return null;
    
    return flow.steps.find(s => s.type === stepType) || null;
  }
  
  /**
   * Gets the next step after the given step type
   */
  getNextStepType(currentStepType: string): string | null {
    const steps = this.enabledSteps();
    const currentIndex = steps.findIndex(s => s.type === currentStepType);
    
    if (currentIndex === -1 || currentIndex === steps.length - 1) {
      return null;
    }
    
    return steps[currentIndex + 1].type;
  }
  
  /**
   * Gets the previous step before the given step type
   */
  getPreviousStepType(currentStepType: string): string | null {
    const steps = this.enabledSteps();
    const currentIndex = steps.findIndex(s => s.type === currentStepType);
    
    if (currentIndex <= 0) {
      return null;
    }
    
    return steps[currentIndex - 1].type;
  }
  
  /**
   * Clears the current flow (for cleanup)
   */
  clear(): void {
    this._flow.set(null);
    this._campaign.set(null);
  }
}
